<script setup lang="ts">
import type {
	ContentShowcaseHeroEvents,
	ContentShowcaseHeroProps,
	SharingOption
} from '.';

import { onClickOutside, useClipboard } from '@vueuse/core';
import { grammarInflection } from '~~/utils';
import { h } from 'vue';

import { CopyToastDescription } from '~/components/CopyToastDescription';
import { HeroButton } from '~/components/HeroButton';
import { HighlightBadge } from '~/components/HighlightBadge';
import { ShareContent } from '~/components/ShareContent';
import { Button } from '~/components/ui/button';
import { Skeleton } from '~/components/ui/skeleton';
import { useToast } from '~/components/ui/toast';

import IconMaterialSymbolsLightImageOutline from '~icons/material-symbols-light/image-outline';
import IconMaterialSymbolsLightMapOutline from '~icons/material-symbols-light/map-outline';
import IconMaterialSymbolsPlayArrowOutline from '~icons/material-symbols-light/play-arrow-outline';
import IconMaterialSymbolsShare from '~icons/material-symbols-light/share-outline';

import { gridColumnVariants } from './variants';

const props = withDefaults(defineProps<ContentShowcaseHeroProps>(), {
	title: '',
	description: '',
	quoteText: '',
	quoteAuthor: '',
	price: 0,
	priceLabel: 'Valor',
	isLoading: false,
	isExclusive: false,
	images: () => [],
	videos: () => [],
	ctaText: 'Entre em contato',
	shareUrl: '',
	showMapButton: true,
	formatPrice: (value: number) =>
		new Intl.NumberFormat('pt-BR', {
			style: 'currency',
			currency: 'BRL'
		}).format(value),
	imageAltGenerator: (index: number) =>
		`Imagem ${index !== null ? index + 1 : ''}`,
	firstColumnWidth: 'default'
});

const emit = defineEmits<ContentShowcaseHeroEvents>();

const { copy } = useClipboard();
const { toast } = useToast();

const showShareContent = ref(false);
const shareContentRef = ref<HTMLElement | null>(null);
const shareContentButton = ref<HTMLElement | null>(null);

const firstImage = computed<string | null>(() => props.images[0] ?? null);

const hasSecondImage = computed<boolean>(() =>
	props.isLoading ? true : !!props.images[1]
);

const numberOfImages = computed<number>(() => props.images.length ?? 0);

const numberOfVideos = computed<number>(() => props.videos.length ?? 0);

const photoButtonText = computed<string | undefined>(() => {
	if (!numberOfImages.value) return undefined;

	return `${numberOfImages.value} ${grammarInflection(numberOfImages.value, 'foto', 'fotos')}`;
});

const videoButtonText = computed<string | undefined>(() => {
	if (!numberOfVideos.value) return undefined;

	return `${numberOfVideos.value} ${grammarInflection(numberOfVideos.value, 'vídeo', 'vídeos')}`;
});

const gridColumnClasses = computed(() =>
	gridColumnVariants({
		width: props.firstColumnWidth,
		singleImage: props.images.length <= 1
	})
);

function toggleShareContent() {
	showShareContent.value = !showShareContent.value;
}

function handleShareClicked(option: SharingOption) {
	emit('shareClick', option);
}

function shareMobile() {
	emit('shareButtonClick');
	const data = {
		title: props.title,
		url: props.shareUrl
	};
	if (navigator?.share && navigator?.canShare(data)) {
		navigator.share(data);
	} else {
		copy(props.shareUrl);
		toast({
			description: h(CopyToastDescription)
		});
	}
}

onClickOutside(shareContentRef, () => (showShareContent.value = false), {
	ignore: [shareContentButton]
});
</script>

<template>
	<section
		class="relative flex h-[72dvh] min-h-[500px] w-full overflow-hidden bg-primary md:max-h-[500px] lg:h-[85dvh]"
	>
		<div
			class="absolute bottom-[90px] z-20 flex flex-col items-start justify-center pl-5 pr-3 md:relative md:bottom-0 md:w-3/5 md:border-t md:border-primary-light-4 md:pl-12 lg:w-2/5"
		>
			<div
				v-if="isLoading"
				class="relative z-20 flex size-full flex-col justify-between gap-2 py-4 md:py-6"
			>
				<div class="w-full space-y-3">
					<Skeleton
						v-for="index in 2"
						:key="`hero-skeleton-line-${index}`"
						:is-active="true"
						variant="primary-light-1"
						class="h-[30px] w-11/12 last:w-6/12"
					/>

					<Skeleton
						:is-active="true"
						variant="primary-light-1"
						class="h-[30px] w-6/12"
					/>

					<Skeleton
						:is-active="true"
						variant="primary-light-1"
						class="mt-6! h-4 w-11/12"
					/>
				</div>
				<div class="space-y-6">
					<Skeleton
						:is-active="true"
						variant="primary-light-1"
						class="h-3 w-3/12"
					/>

					<Skeleton
						:is-active="true"
						variant="primary-light-1"
						class="h-[30px] w-6/12"
					/>

					<Skeleton
						:is-active="true"
						variant="primary-light-1"
						class="h-12 w-9/12"
					/>
				</div>
			</div>

			<div
				v-else
				class="relative flex size-full flex-col justify-between overflow-hidden"
			>
				<div class="flex size-full flex-col items-start gap-2 md:pr-8 md:pt-6">
					<div
						v-if="isExclusive"
						class="order-1 h-min max-w-64 pt-2 md:order-2 md:max-w-72"
					>
						<HighlightBadge />
					</div>
					<h1
						class="order-2 line-clamp-4 font-matter-sq text-2xl font-normal leading-[150%] tracking-[-0.03rem] text-white md:order-1 lg:text-[2rem] lg:font-light lg:tracking-[-0.04rem]"
					>
						{{ title }}
					</h1>

					<slot name="description" :description="description" />
				</div>
				<div class="relative hidden min-h-fit flex-col gap-3 pb-6 pr-6 md:flex">
					<div v-if="price" class="flex flex-col">
						<h3 class="font-sans text-sm font-normal text-white-dark-4">
							{{ priceLabel }}
						</h3>
						<span
							class="font-matter-sq text-[32px] font-normal tracking-[-0.64px] text-beige"
						>
							{{
								formatPrice(price, {
									minimumFractionDigits: 0,
									maximumFractionDigits: 0,
									roundingMode: 'ceil'
								})
							}}
						</span>
					</div>
					<div class="hidden w-full flex-col items-start md:flex">
						<slot name="ctaButton">
							<Button
								variant="primary-dark"
								class="w-fit px-12 py-3"
								@click="emit('ctaClick')"
							>
								{{ ctaText }}
							</Button>
						</slot>
					</div>
				</div>
			</div>
		</div>

		<div
			class="relative flex size-full flex-wrap overflow-hidden bg-white md:w-4/5"
		>
			<div :class="gridColumnClasses">
				<div class="relative col-span-1 min-h-[500px] w-full">
					<div
						class="absolute bottom-0 z-20 flex w-full items-center justify-start gap-4 px-[20px] pb-6 lg:w-1/2 2xl:w-2/5"
					>
						<HeroButton
							v-if="photoButtonText"
							class="max-w-32 text-sm font-semibold"
							@click="emit('photosButtonClick', { photoCount: numberOfImages })"
						>
							<template #icon>
								<IconMaterialSymbolsLightImageOutline
									class="size-5 text-beige"
								/>
							</template>
							{{ photoButtonText }}
						</HeroButton>

						<HeroButton
							v-if="numberOfVideos"
							class="max-w-32 text-sm font-semibold"
							@click="emit('videosButtonClick')"
						>
							<template #icon>
								<IconMaterialSymbolsPlayArrowOutline
									class="size-5 text-beige"
								/>
							</template>
							{{ videoButtonText }}
						</HeroButton>

						<HeroButton
							v-if="showMapButton"
							class="max-w-32 text-sm font-semibold"
							@click="emit('mapClick')"
						>
							<template #icon>
								<IconMaterialSymbolsLightMapOutline class="size-5 text-beige" />
							</template>
							Mapa
						</HeroButton>
					</div>
					<span
						class="absolute inset-0 z-10 size-full cursor-pointer bg-[linear-gradient(0deg,rgba(0,0,0,0.2)_0%,rgba(0,0,0,0.2)_100%),linear-gradient(180deg,rgba(0,0,0,0)_0%,rgba(0,0,0,0.4)_100%)] md:hidden"
						@click="
							emit('photoClick', {
								photoIndex: 0,
								photoUrl: firstImage || undefined
							})
						"
					>
					</span>

					<Skeleton :is-active="isLoading" class="size-full" variant="white">
						<slot name="firstPosition">
							<NuxtImg
								v-if="firstImage"
								:src="firstImage"
								:alt="imageAltGenerator(0)"
								width="960"
								height="500"
								sizes="xs:375px sm:640px md:480px lg:600px xl:720px 2xl:960px"
								densities="x1"
								class="size-full cursor-pointer object-cover"
								:placeholder="[50, 25]"
								format="webp"
								quality="80"
								preload
								loading="lazy"
								@click="
									emit('photoClick', { photoIndex: 0, photoUrl: firstImage })
								"
							/>
						</slot>
					</Skeleton>
				</div>
				<div
					v-if="hasSecondImage"
					class="relative hidden h-full grid-cols-[repeat(1,1fr)] grid-rows-[repeat(2,minmax(248px,1fr))] gap-1 overflow-hidden sm:grid"
				>
					<div>
						<Skeleton :is-active="isLoading" class="size-full" variant="white">
							<NuxtImg
								v-if="images[1]"
								:src="images[1]"
								:alt="imageAltGenerator(1)"
								width="384"
								height="248"
								sizes="xs:375px sm:320px md:384px"
								densities="x1"
								class="size-full cursor-pointer object-cover"
								:placeholder="[50, 25]"
								format="webp"
								quality="80"
								preload
								loading="lazy"
								@click="
									emit('photoClick', {
										photoIndex: 1,
										photoUrl: images[1]
									})
								"
							/>
						</Skeleton>
					</div>

					<slot name="thirdPosition">
						<Skeleton
							v-if="isLoading"
							:is-active="true"
							class="size-full"
							variant="white"
						/>
						<NuxtImg
							v-else-if="images[2]"
							:src="images[2]"
							:alt="imageAltGenerator(2)"
							width="384"
							height="248"
							sizes="xs:375px sm:320px md:384px"
							densities="x1"
							class="size-full cursor-pointer object-cover"
							:placeholder="[50, 25]"
							format="webp"
							quality="80"
							preload
							loading="lazy"
							@click="
								emit('photoClick', {
									photoIndex: 2,
									photoUrl: images[2]
								})
							"
						/>
					</slot>
				</div>
			</div>

			<div
				class="absolute right-10 top-10 z-20 size-5 cursor-pointer md:right-20 md:top-5"
			>
				<div class="flex flex-col">
					<button
						ref="shareContentButton"
						class="z-10 hidden size-11 w-fit items-center justify-center border border-white-dark-3 bg-white px-[14px] py-2 md:flex"
						@click="toggleShareContent"
					>
						<IconMaterialSymbolsShare
							class="z-20 size-5 min-h-5 min-w-5 fill-primary text-primary"
						/>
					</button>
					<button
						class="z-10 flex size-11 w-fit items-center justify-center bg-white px-[14px] py-2 md:hidden"
						@click="shareMobile"
					>
						<IconMaterialSymbolsShare
							class="z-20 size-5 min-h-5 min-w-5 fill-primary text-primary"
						/>
					</button>
				</div>

				<div
					v-show="showShareContent"
					ref="shareContentRef"
					class="absolute right-[-30px] top-[-42px] w-[125px] translate-y-[60%] border border-white-dark-3 bg-white p-[0.7rem_1rem]"
				>
					<ShareContent
						v-model="showShareContent"
						variant="dropdown"
						mail-subject="Leia mais sobre este conteúdo"
						:content-to-share="shareUrl"
						show-labels
						@share-clicked="handleShareClicked"
					/>
				</div>
			</div>
		</div>
	</section>
</template>
